import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { SketchPicker, TwitterPicker } from 'react-color';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const PersonalizacionTemaForm = ({
  colorPrimario = '#1976d2',
  colorSecundario = '#dc004e',
  handleColorChange,
  errors = {},
  showPreview = true,
  allowPresets = true,
  allowReset = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Estados locales
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copiedColor, setCopiedColor] = useState(null);
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [previewDark, setPreviewDark] = useState(false);

  // Paleta de colores predefinidos
  const colorPresets = [
    { name: 'Material Blue', primary: '#1976d2', secondary: '#dc004e' },
    { name: 'Purple Passion', primary: '#7b1fa2', secondary: '#ff5722' },
    { name: 'Green Nature', primary: '#388e3c', secondary: '#ff9800' },
    { name: 'Red Energy', primary: '#d32f2f', secondary: '#3f51b5' },
    { name: 'Orange Sunset', primary: '#f57c00', secondary: '#e91e63' },
    { name: 'Teal Ocean', primary: '#00796b', secondary: '#ff5722' },
    { name: 'Indigo Night', primary: '#303f9f', secondary: '#ffc107' },
    { name: 'Pink Rose', primary: '#c2185b', secondary: '#4caf50' },
  ];

  // Colores populares para TwitterPicker
  const popularColors = [
    '#1976d2', '#7b1fa2', '#388e3c', '#d32f2f', '#f57c00', '#00796b',
    '#303f9f', '#c2185b', '#ff5722', '#ff9800', '#e91e63', '#3f51b5',
    '#4caf50', '#ffc107', '#795548', '#607d8b', '#000000', '#424242',
  ];

  // Crear tema de previsualización
  const previewTheme = useMemo(() => {
    try {
      return createTheme({
        palette: {
          mode: previewDark ? 'dark' : 'light',
          primary: { main: colorPrimario },
          secondary: { main: colorSecundario },
        },
      });
    } catch (error) {
      console.warn('Error creando tema de preview:', error);
      return theme;
    }
  }, [colorPrimario, colorSecundario, previewDark, theme]);

  // Validación de accesibilidad (contraste)
  const checkContrast = (color) => {
    try {
      // Convertir hex a RGB
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      // Luminancia relativa (WCAG)
      const srgb = [r, g, b].map(v => {
        v = v / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      const luminance = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];

      const contrastWithWhite = (1.05) / (luminance + 0.05);
      const contrastWithBlack = (luminance + 0.05) / 0.05;
      const bestOnColor = contrastWithWhite >= contrastWithBlack ? '#ffffff' : '#000000';
      const bestContrast = Math.max(contrastWithWhite, contrastWithBlack);
      const passesAA = bestContrast >= 4.5; // criterio aproximado texto normal

      return {
        isLight: luminance > 0.5,
        contrastRatio: bestContrast,
        contrast: bestContrast >= 7 ? 'excelente' : bestContrast >= 4.5 ? 'bueno' : 'bajo',
        accessible: passesAA,
        onColor: bestOnColor,
      };
    } catch {
      return { isLight: true, contrast: 'desconocido', accessible: true, contrastRatio: 0, onColor: '#000' };
    }
  };

  const primaryContrast = checkContrast(colorPrimario);
  const secondaryContrast = checkContrast(colorSecundario);

  // Handlers mejorados
  const handlePrimaryChange = (color) => {
    handleColorChange('colorPrimario', color.hex);
  };

  const handleSecondaryChange = (color) => {
    handleColorChange('colorSecundario', color.hex);
  };

  // Utilidades de color
  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
  const hexToHsl = (hex) => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substr(0,2),16)/255;
    const g = parseInt(h.substr(2,2),16)/255;
    const b = parseInt(h.substr(4,2),16)/255;
    const max = Math.max(r,g,b), min = Math.min(r,g,b);
    let hDeg, s, l = (max+min)/2;
    if (max === min) { hDeg = s = 0; }
    else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: hDeg = (g - b) / d + (g < b ? 6 : 0); break;
        case g: hDeg = (b - r) / d + 2; break;
        case b: hDeg = (r - g) / d + 4; break;
      }
      hDeg *= 60;
    }
    return { h: hDeg, s, l };
  };
  const hslToHex = ({h,s,l}) => {
    const c = (1 - Math.abs(2*l - 1)) * s;
    const x = c * (1 - Math.abs(((h/60) % 2) - 1));
    const m = l - c/2;
    let r=0,g=0,b=0;
    if (0<=h && h<60) { r=c; g=x; b=0; }
    else if (60<=h && h<120) { r=x; g=c; b=0; }
    else if (120<=h && h<180) { r=0; g=c; b=x; }
    else if (180<=h && h<240) { r=0; g=x; b=c; }
    else if (240<=h && h<300) { r=x; g=0; b=c; }
    else { r=c; g=0; b=x; }
    const toHex = (v)=> ('0' + Math.round((v+m)*255).toString(16)).slice(-2);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };
  const lighten = (hex, amt=0.1) => {
    const hsl = hexToHsl(hex); hsl.l = clamp(hsl.l + amt, 0, 1); return hslToHex(hsl);
  };
  const darken = (hex, amt=0.1) => {
    const hsl = hexToHsl(hex); hsl.l = clamp(hsl.l - amt, 0, 1); return hslToHex(hsl);
  };
  const complement = (hex) => {
    const hsl = hexToHsl(hex); hsl.h = (hsl.h + 180) % 360; return hslToHex(hsl);
  };

  const suggestions = useMemo(() => {
    return [
      { label: 'Complementario', value: complement(colorPrimario) },
      { label: 'Más claro', value: lighten(colorPrimario, 0.2) },
      { label: 'Más oscuro', value: darken(colorPrimario, 0.2) },
    ];
  }, [colorPrimario]);

  const handlePresetApply = (preset) => {
    handleColorChange('colorPrimario', preset.primary);
    handleColorChange('colorSecundario', preset.secondary);
  };

  const handleReset = () => {
    handleColorChange('colorPrimario', '#1976d2');
    handleColorChange('colorSecundario', '#dc004e');
  };

  const handleCopyColor = async (color, type) => {
    try {
      await navigator.clipboard.writeText(color);
      setCopiedColor(type);
      setTimeout(() => setCopiedColor(null), 2000);
    } catch (error) {
      console.warn('Error copiando color:', error);
    }
  };

  // Componente de preview de color
  const ColorPreview = ({ color, label, onCopy, contrast, error }) => (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight="medium">
            {label}
          </Typography>
          
          {/* Vista previa del color */}
          <Box
            sx={{
              width: '100%',
              height: 60,
              borderRadius: 1,
              backgroundColor: color,
              border: `2px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
            onClick={() => onCopy(color, label.toLowerCase())}
          >
            {/* Texto del color */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                right: 4,
                color: contrast.isLight ? 'black' : 'white',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                textAlign: 'center',
                backgroundColor: contrast.isLight ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                borderRadius: '4px',
                py: 0.25,
              }}
            >
              {color.toUpperCase()}
            </Box>
          </Box>

          {/* Información del contraste */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Chip
              size="small"
              label={`Contraste: ${contrast.contrast}`}
              color={contrast.contrast === 'bueno' ? 'success' : 'warning'}
              variant="outlined"
            />
            
            <Tooltip title={copiedColor === label.toLowerCase() ? '¡Copiado!' : 'Copiar color'}>
              <IconButton 
                size="small" 
                onClick={() => onCopy(color, label.toLowerCase())}
              >
                {copiedColor === label.toLowerCase() ? <CheckIcon /> : <CopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          {error && (
            <Alert severity="error" sx={{ fontSize: '0.75rem', py: 0.5 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  // Componente de previsualización del tema
  const ThemePreview = () => (
    <ThemeProvider theme={previewTheme}>
      <Paper
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h6" color="primary">
            Vista Previa del Tema
          </Typography>
          <FormControlLabel
            control={<Switch size="small" checked={previewDark} onChange={(e)=>setPreviewDark(e.target.checked)} />}
            label={previewDark ? 'Modo oscuro' : 'Modo claro'}
          />
        </Box>
        
        <Stack spacing={2}>
          <Button variant="contained" color="primary" size="small">
            Botón Primario
          </Button>
          
          <Button variant="contained" color="secondary" size="small">
            Botón Secundario
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label="Chip Primario" color="primary" />
            <Chip label="Chip Secundario" color="secondary" />
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Así se verán los elementos con estos colores en tu evento.
          </Typography>
        </Stack>
      </Paper>
    </ThemeProvider>
  );

  return (
    <Box>
      <Grid container spacing={3} disableEqualOverflow>
        {/* Selectores de Color */}
        <Grid item xs={12} lg={showPreview ? 8 : 12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Typography variant="h6" fontWeight="medium">
                Personalización de Colores
              </Typography>
            </Box>

            {/* Entrada manual de HEX + acciones */}
            <Grid container spacing={2} disableEqualOverflow mb={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Primario (HEX)"
                  value={colorPrimario}
                  onChange={(e)=> handleColorChange('colorPrimario', e.target.value.startsWith('#')? e.target.value : `#${e.target.value}`)}
                  InputProps={{ inputProps: { maxLength: 7 } }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Secundario (HEX)"
                  value={colorSecundario}
                  onChange={(e)=> handleColorChange('colorSecundario', e.target.value.startsWith('#')? e.target.value : `#${e.target.value}`)}
                  InputProps={{ inputProps: { maxLength: 7 } }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md sx={{ display: 'flex', alignItems: 'center' }}>
                <Button size="small" variant="outlined" onClick={()=>{ const p=colorPrimario; handleColorChange('colorPrimario', colorSecundario); handleColorChange('colorSecundario', p); }}>
                  Intercambiar
                </Button>
              </Grid>
            </Grid>

            {/* Vista previa de colores */}
            <Grid container spacing={2} disableEqualOverflow mb={3}>
              <Grid item xs={12} sm={6}>
                <ColorPreview
                  color={colorPrimario}
                  label="Color Primario"
                  onCopy={handleCopyColor}
                  contrast={primaryContrast}
                  error={errors.colorPrimario}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <ColorPreview
                  color={colorSecundario}
                  label="Color Secundario"
                  onCopy={handleCopyColor}
                  contrast={secondaryContrast}
                  error={errors.colorSecundario}
                />
              </Grid>
            </Grid>

            {/* Botones de acción */}
            <Box display="flex" gap={1} mb={3} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExpandMoreIcon />}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? 'Ocultar' : 'Mostrar'} Avanzado
              </Button>
              
              {allowReset && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                >
                  Restablecer
                </Button>
              )}
            </Box>

            {/* Selectores rápidos */}
            {!showAdvanced && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selección Rápida
                </Typography>
                
                {/* Color Primario */}
                <Box mb={2}>
                  <Typography variant="body2" gutterBottom>
                    Color Primario
                  </Typography>
                  <TwitterPicker
                    color={colorPrimario}
                    colors={popularColors}
                    onChangeComplete={handlePrimaryChange}
                    width="100%"
                    triangle="hide"
                  />
                </Box>

                {/* Color Secundario */}
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Color Secundario
                  </Typography>
                  <TwitterPicker
                    color={colorSecundario}
                    colors={popularColors}
                    onChangeComplete={handleSecondaryChange}
                    width="100%"
                    triangle="hide"
                  />
                </Box>
              </Box>
            )}

            {/* Selectores avanzados */}
            <Collapse in={showAdvanced}>
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={3}>
                {/* Selector Primario Avanzado */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color Primario - Selector Avanzado
                  </Typography>
                  <Box sx={{ 
                    '& .sketch-picker': { 
                      width: '100% !important',
                      boxShadow: 'none !important',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '8px',
                    }
                  }}>
                    <SketchPicker
                      color={colorPrimario}
                      onChangeComplete={handlePrimaryChange}
                      disableAlpha
                    />
                  </Box>
                </Grid>

                {/* Selector Secundario Avanzado */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color Secundario - Selector Avanzado
                  </Typography>
                  <Box sx={{ 
                    '& .sketch-picker': { 
                      width: '100% !important',
                      boxShadow: 'none !important',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '8px',
                    }
                  }}>
                    <SketchPicker
                      color={colorSecundario}
                      onChangeComplete={handleSecondaryChange}
                      disableAlpha
                    />
                  </Box>
                </Grid>
              </Grid>
            </Collapse>

            {/* Paletas predefinidas */}
            {allowPresets && (
              <Box mt={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Paletas Predefinidas
                </Typography>
                
                <Grid container spacing={1}>
                  {colorPresets.map((preset, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': { transform: 'scale(1.02)', boxShadow: 2 }
                        }}
                        onClick={() => handlePresetApply(preset)}
                      >
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Box display="flex" mb={1}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: preset.primary,
                                borderRadius: '4px 0 0 4px',
                                border: `1px solid ${theme.palette.divider}`,
                              }}
                            />
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                backgroundColor: preset.secondary,
                                borderRadius: '0 4px 4px 0',
                                border: `1px solid ${theme.palette.divider}`,
                                borderLeft: 'none',
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            {preset.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Sugerencias a partir del primario */}
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                Sugerencias basadas en primario
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {suggestions.map((sug) => (
                  <Button key={sug.label} size="small" variant="outlined" onClick={()=>handleColorChange('colorSecundario', sug.value)}
                    startIcon={<PaletteIcon fontSize="small" />}
                    sx={{ textTransform: 'none' }}
                  >
                    {sug.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Paper>
        </Grid>

        {/* Vista Previa */}
        {showPreview && (
          <Grid item xs={12} lg={4}>
            <Box position="sticky" top={20}>
              <ThemePreview />
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Alertas de accesibilidad */}
      {(!primaryContrast.accessible || !secondaryContrast.accessible) && (
        <Alert 
          severity="warning" 
          sx={{ mt: 2 }}
          icon={<WarningIcon />}
        >
          Algunos colores seleccionados pueden tener problemas de contraste. 
          Considera usar colores con mejor legibilidad para una mejor experiencia de usuario.
        </Alert>
      )}
    </Box>
  );
};

PersonalizacionTemaForm.propTypes = {
  colorPrimario: PropTypes.string,
  colorSecundario: PropTypes.string,
  handleColorChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  showPreview: PropTypes.bool,
  allowPresets: PropTypes.bool,
  allowReset: PropTypes.bool,
};

export default PersonalizacionTemaForm;
